import React, { useState, useEffect } from 'react';
import { X, Edit, Calendar, FileText, CheckCircle, Trash2 } from 'lucide-react';
import { fetchSolutions } from '../utils/supabaseHelpers';
import { formatSimpleDate } from '../utils/dateFormatter';

const ProblemDetailModal = ({ problem, onClose, onEdit, onUpdate, onDelete }) => {
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [newStatus, setNewStatus] = useState(problem.status);
  const [solutions, setSolutions] = useState([]);
  const [loadingSolutions, setLoadingSolutions] = useState(false);

  useEffect(() => {
    // Use solutions from problem object (loaded via LEFT JOIN) or fallback to empty array
    const problemSolutions = problem.solutions || [];
    setSolutions(problemSolutions);
    setLoadingSolutions(false);
  }, [problem]);

  const handleStatusUpdate = () => {
    onUpdate({ ...problem, status: newStatus });
    setShowStatusUpdate(false);
  };

  const getDomainStyle = (domain) => {
    const d = domain?.toLowerCase() || '';
    switch(d) {
      case 'electronics': return 'border border-blue-400 text-blue-400 bg-blue-400/10';
      case 'mechanical': return 'border border-purple-400 text-purple-400 bg-purple-400/10';
      case 'software': return 'border border-green-400 text-green-400 bg-green-400/10';
      case 'management': return 'border border-orange-400 text-orange-400 bg-orange-400/10';
      default: return 'border border-gray-400 text-gray-400 bg-gray-400/10';
    }
  };

  const getStatusStyle = (status) => {
    switch(status) {
      case 'Not Decided': return 'border border-gray-400 text-gray-400 bg-gray-400/10';
      case 'In Progress': return 'border border-blue-400 text-blue-400 bg-blue-400/10';
      case 'Done': return 'border border-green-400 text-green-400 bg-green-400/10';
      default: return 'border border-gray-400 text-gray-400 bg-gray-400/10';
    }
  };

  const getPriorityStyle = (priority) => {
    const p = priority?.toLowerCase() || '';
    switch(p) {
      case 'critical': return 'border border-red-400 text-red-400 bg-red-400/10';
      case 'high': return 'border border-orange-400 text-orange-400 bg-orange-400/10';
      case 'medium': return 'border border-yellow-400 text-yellow-400 bg-yellow-400/10';
      case 'low': return 'border border-green-400 text-green-400 bg-green-400/10';
      default: return 'border border-gray-400 text-gray-400 bg-gray-400/10';
    }
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="problem-detail-modal">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-2xl font-bold flex-1 pr-4" data-testid="modal-title">{problem.title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="modal-close-btn"
            >
              <X size={24} />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDomainStyle(problem.domain)}`}>
              {problem.domain?.charAt(0).toUpperCase() + problem.domain?.slice(1)}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusStyle(problem.status)}`}>
              {formatStatus(problem.status)}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityStyle(problem.priority)}`}>
              {problem.priority?.charAt(0).toUpperCase() + problem.priority?.slice(1)}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 scrollbar-thin" style={{ maxHeight: 'calc(90vh - 180px)', overflowY: 'auto' }}>


          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <FileText size={20} className="text-blue-400" />
              Description
            </h3>
            <p className="text-gray-300" data-testid="modal-description">{problem.description}</p>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-2">
                <Calendar size={16} />
                <span className="text-sm">Date Reported</span>
              </div>
              <p className="font-medium" data-testid="modal-date">
                {formatSimpleDate(problem.date_reported || problem.dateReported || problem.created_at)}
              </p>
            </div>
            
            {problem.updated_at && problem.updated_at !== problem.created_at && (
              <div className="glass-card p-4">
                <div className="flex items-center gap-2 text-gray-400 mb-2">
                  <Calendar size={16} />
                  <span className="text-sm">Last Updated</span>
                </div>
                <p className="font-medium" data-testid="modal-updated-date">
                  {formatSimpleDate(problem.updated_at)}
                </p>
              </div>
            )}
            
            {(problem.due_date || problem.dueDate) && (
              <div className="glass-card p-4">
                <div className="flex items-center gap-2 text-gray-400 mb-2">
                  <Calendar size={16} />
                  <span className="text-sm">Due Date</span>
                </div>
                <p className="font-medium" data-testid="modal-due-date">
                  {formatSimpleDate(problem.due_date || problem.dueDate)}
                </p>
              </div>
            )}
          </div>

          {/* Solutions Section */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4 text-white">Solutions</h2>
            {problem?.solutions && problem.solutions.length > 0 ? (
              <div className="space-y-3">
                {problem.solutions.map((solution, index) => (
                  <div 
                    key={solution.id || index}
                    className={`p-4 rounded ${
                      solution.is_chosen 
                        ? 'bg-green-900/20 border-2 border-green-500' 
                        : 'bg-gray-800 border border-gray-700'
                    }`}
                  >
                    <p className="text-gray-200">{solution.description}</p>
                    {solution.is_chosen && (
                      <span className="text-sm text-green-400 font-semibold mt-2 inline-block">
                        ✓ Chosen Solution
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400">No solutions added yet.</p>
            )}
          </div>

          {/* Notes */}
          {problem.notes && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Notes</h3>
              <div className="glass-card p-4">
                <p className="text-gray-300 whitespace-pre-wrap" data-testid="modal-notes">{problem.notes}</p>
              </div>
            </div>
          )}

          {/* Documents */}
          {problem.linkedDocuments && problem.linkedDocuments.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Linked Documents</h3>
              <div className="glass-card p-4">
                <p className="text-gray-400 text-sm">{problem.linkedDocuments.length} document(s) linked</p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-white/10 flex gap-3">
          <button
            onClick={onEdit}
            className="btn-primary flex items-center gap-2"
            data-testid="modal-edit-btn"
          >
            <Edit size={18} />
            Edit
          </button>
          <button
            onClick={() => setShowStatusUpdate(!showStatusUpdate)}
            className="btn-secondary"
            data-testid="modal-update-status-btn"
          >
            Update Status
          </button>
          {onDelete && (
            <button
              onClick={onDelete}
              className="btn-secondary text-red-400 hover:bg-red-500/10 flex items-center gap-2 ml-auto"
              data-testid="modal-delete-btn"
            >
              <Trash2 size={18} />
              Delete
            </button>
          )}
          {showStatusUpdate && (
            <div className="flex-1 flex gap-2">
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="glass-input flex-1"
                data-testid="status-select"
              >
                <option value="Not Decided">Not Decided</option>
                <option value="In Progress">In Progress</option>
                <option value="Done">Done</option>
              </select>
              <button
                onClick={handleStatusUpdate}
                className="btn-primary"
                data-testid="status-update-confirm-btn"
              >
                Update
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProblemDetailModal;
